"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Job } from "@/lib/api/types";
import {
  archiveJob as archiveJobApi,
  closeJob as closeJobApi,
  getJob,
  reopenJob as reopenJobApi,
} from "@/lib/api/generated";
import { isPermissionDenied, getPermissionErrorMessage } from "@/lib/permissions";
import { useJobWizardStore } from "@/features/jobs/store/job-store";
import { jobToWizardPrefill } from "@/features/jobs/lib/clone";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

export interface UseJobRowActionsResult {
  openJob: (job: Job) => void;
  cloneJob: (job: Job) => void;
  cloningJobId: string | null;
  closeJob: (jobId: string) => void;
  reopenJob: (jobId: string) => void;
  requestArchive: (job: Job) => void;
  confirmArchive: () => void;
  cancelArchive: () => void;
  archiveTarget: Job | null;
  archivePending: boolean;
}

/**
 * Encapsulates row-level job actions (close, reopen, archive, open/resume wizard)
 * so list table components stay presentational.
 */
export function useJobRowActions(): UseJobRowActionsResult {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { resumeWizard, openClone } = useJobWizardStore();
  const [archiveTarget, setArchiveTarget] = useState<Job | null>(null);
  const [cloningJobId, setCloningJobId] = useState<string | null>(null);

  const openJob = useCallback(
    (job: Job) => {
      if (job.status === "draft") {
        const nextStep = Math.min((job.wizard_step || 1) + 1, 4) as 1 | 2 | 3 | 4;
        const apiStages = job.stages?.length
          ? [...job.stages]
              .sort((a, b) => a.position - b.position)
              .map((s) => ({
                id: s.id,
                name: s.name,
                color: s.color,
                description: s.description ?? "",
                is_system: s.is_system,
              }))
          : undefined;
        resumeWizard(
          job.id,
          nextStep,
          {
            title: job.title,
            description: job.description || "",
            managerId: job.job_manager_id,
            ...(apiStages ? { stages: apiStages } : {}),
          },
          job.project_id ?? null,
        );
      } else {
        router.push(`/jobs/${job.id}`);
      }
    },
    [resumeWizard, router],
  );

  const cloneJob = useCallback(
    async (job: Job) => {
      if (cloningJobId) return;
      setCloningJobId(job.id);
      try {
        const source = await getJob(job.id);
        const form = jobToWizardPrefill(source);
        openClone({
          sourceId: source.id,
          sourceName: source.title,
          form,
          projectId: source.project_id ?? null,
        });
      } catch (err) {
        showToast.error("Couldn't open the clone wizard. Please try again.", {
          description: getApiErrorMessage(err),
        });
      } finally {
        setCloningJobId(null);
      }
    },
    [cloningJobId, openClone],
  );

  const closeJob = useMutation({
    mutationFn: (jobId: string) => closeJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      showToast.success("Job closed");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error("Failed to close job", { description: getApiErrorMessage(err) });
      }
    },
  });

  const reopenJob = useMutation({
    mutationFn: (jobId: string) => reopenJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      showToast.success("Job reopened");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error("Failed to reopen job", { description: getApiErrorMessage(err) });
      }
    },
  });

  const archiveJob = useMutation({
    mutationFn: (jobId: string) => archiveJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setArchiveTarget(null);
      showToast.success("Job archived");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err)) {
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      } else {
        showToast.error("Failed to archive job", { description: getApiErrorMessage(err) });
      }
    },
  });

  const requestArchive = useCallback((job: Job) => setArchiveTarget(job), []);
  const cancelArchive = useCallback(() => setArchiveTarget(null), []);
  const confirmArchive = useCallback(() => {
    if (archiveTarget) archiveJob.mutate(archiveTarget.id);
  }, [archiveTarget, archiveJob]);

  return {
    openJob,
    cloneJob,
    cloningJobId,
    closeJob: closeJob.mutate,
    reopenJob: reopenJob.mutate,
    requestArchive,
    confirmArchive,
    cancelArchive,
    archiveTarget,
    archivePending: archiveJob.isPending,
  };
}
